import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Table, Tooltip } from 'antd';
import { listResidences, createResidence, updateResidence, deleteResidence } from '../../api/residences';
import { getCurrentSemestre } from '../../api/semestres';
import { adminCreateResidenceBuilding, adminDeleteBuilding, adminListResidenceBuildings, adminUpdateBuilding } from '../../api/buildings';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard, { AntPagination } from '../../components/TableCard';
import { EditIcon, DeleteIcon } from '../../components/UIKit';

const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

export default function Residences() {
  const { message, notification } = App.useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [buildingsByResidenceId, setBuildingsByResidenceId] = useState({});
  const [buildingsOpen, setBuildingsOpen] = useState(false);
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [buildingsResidence, setBuildingsResidence] = useState(null);
  const [buildingsRows, setBuildingsRows] = useState([]);
  const [buildingsPage, setBuildingsPage] = useState(1);
  const buildingsPageSize = 8;
  const [buildingOpen, setBuildingOpen] = useState(false);
  const [buildingEditing, setBuildingEditing] = useState(null);
  const [buildingForm] = Form.useForm();
  const [currentSemestre, setCurrentSemestre] = useState(null);
  const [form] = Form.useForm();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [res, current] = await Promise.all([listResidences({}), getCurrentSemestre()]);
      setRows(res); setCurrentSemestre(current);
      const pairs = await Promise.all((res || []).map(async (r) => {
        try { const bs = await adminListResidenceBuildings(r.id); return [String(r.id), Array.isArray(bs) ? bs : []]; }
        catch { return [String(r.id), []]; }
      }));
      setBuildingsByResidenceId(Object.fromEntries(pairs));
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les résidences.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onAdd = () => { setEditing(null); form.resetFields(); if (currentSemestre) form.setFieldsValue({ semestre_id: currentSemestre.id }); setOpen(true); };

  const fetchBuildings = useCallback(async (residenceId) => {
    if (!residenceId) return;
    setBuildingsLoading(true);
    try {
      const data = await adminListResidenceBuildings(residenceId);
      const bRows = Array.isArray(data) ? data : [];
      setBuildingsRows(bRows);
      setBuildingsByResidenceId((prev) => ({ ...prev, [String(residenceId)]: bRows }));
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les bâtiments.'), placement: 'topRight' }); setBuildingsRows([]); }
    finally { setBuildingsLoading(false); }
  }, [notification]);

  const onOpenBuildings = useCallback((record) => { setBuildingsResidence(record); setBuildingsRows([]); setBuildingsPage(1); setBuildingsOpen(true); fetchBuildings(record?.id); }, [fetchBuildings]);
  const onAddBuilding = useCallback(() => { setBuildingEditing(null); buildingForm.resetFields(); buildingForm.setFieldsValue({ capacite_chambres: 0 }); setBuildingOpen(true); }, [buildingForm]);
  const onEditBuilding = useCallback((record) => { setBuildingEditing(record); buildingForm.setFieldsValue({ nom: record?.nom, capacite_chambres: record?.capacite_chambres ?? 0 }); setBuildingOpen(true); }, [buildingForm]);
  const onDeleteBuilding = useCallback(async (record) => {
    try { await adminDeleteBuilding(record.id); message.success('Bâtiment supprimé.'); fetchBuildings(buildingsResidence?.id); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression du bâtiment échouée.'), placement: 'topRight' }); }
  }, [buildingsResidence?.id, fetchBuildings, message, notification]);

  const onSubmitBuilding = useCallback(async () => {
    try {
      const v = await buildingForm.validateFields();
      const payload = { nom: v.nom, capacite_chambres: Number(v.capacite_chambres || 0) };
      if (buildingEditing) { await adminUpdateBuilding(buildingEditing.id, payload); message.success('Bâtiment modifié.'); }
      else { await adminCreateResidenceBuilding(buildingsResidence.id, payload); message.success('Bâtiment créé.'); }
      setBuildingOpen(false); fetchBuildings(buildingsResidence?.id);
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement du bâtiment échoué.'), placement: 'topRight' }); }
  }, [buildingEditing, buildingForm, buildingsResidence, fetchBuildings, message, notification]);

  const pagedBuildingsRows = useMemo(() => {
    const start = (buildingsPage - 1) * buildingsPageSize;
    return (buildingsRows || []).slice(start, start + buildingsPageSize);
  }, [buildingsPage, buildingsRows]);

  const onEdit = useCallback((record) => { setEditing(record); form.setFieldsValue(record); setOpen(true); }, [form]);
  const onDelete = useCallback(async (record) => {
    try { await deleteResidence(record.id); message.success('Résidence supprimée.'); fetchAll(); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Suppression de la résidence échouée.'), placement: 'topRight' }); }
  }, [fetchAll, message, notification]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!editing && currentSemestre && !values?.semestre_id) values.semestre_id = currentSemestre.id;
      if (editing) { await updateResidence(editing.id, values); message.success('Résidence modifiée.'); }
      else { await createResidence(values); message.success('Résidence créée.'); }
      setOpen(false); fetchAll();
    } catch (e) { if (e?.response || e?.message) notification.error({ message: getApiErrorMessage(e, 'Enregistrement de la résidence échoué.'), placement: 'topRight' }); }
  };

  return (
    <div className="page">
      <TableCard
        label="Résidences"
        loading={loading}
        rows={rows}
        toolbar={<button className="btn-primary" onClick={onAdd} disabled={!currentSemestre}>+ Ajouter</button>}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Sexe</th>
              <th>Bâtiments (cap.)</th>
              <th>Capacité</th>
              <th>Âge min</th>
              <th>Âge max</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const bs = buildingsByResidenceId?.[String(r?.id)] || [];
              const totalCap = bs.reduce((acc, b) => acc + Number(b?.capacite_chambres || 0), 0);
              const tooltip = bs.length ? bs.map((b) => `${b.nom} — ${b.capacite_chambres}`).join('\n') : 'Aucun bâtiment';
              return (
                <tr key={r.id}>
                  <td>{r.nom}</td>
                  <td>{r.sexe || '-'}</td>
                  <td>
                    <Tooltip title={<pre style={{ margin: 0, fontSize: 11 }}>{tooltip}</pre>} placement="topLeft">
                      <span style={{ cursor: 'help' }}>{bs.length} ({totalCap})</span>
                    </Tooltip>
                  </td>
                  <td>{r.capacite ?? '-'}</td>
                  <td>{r.age_min ?? '-'}</td>
                  <td>{r.age_max ?? '-'}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description || '-'}</td>
                  <td>
                    <div className="actions-cell">
                      <button className="action-btn" title="Bâtiments" onClick={() => onOpenBuildings(r)}><BuildingIcon /></button>
                      <button className="action-btn" title="Modifier" onClick={() => onEdit(r)}><EditIcon /></button>
                      <Popconfirm title="Supprimer cette résidence ?" description={r.nom} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDelete(r)}>
                        <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* Form Drawer */}
      <Drawer title={editing ? 'Modifier résidence' : 'Nouvelle résidence'} placement="right" width={480} onClose={() => setOpen(false)} open={open} destroyOnClose extra={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={onSubmit}>{editing ? 'Enregistrer' : 'Créer'}</button></div>}>
        <Form form={form} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="sexe" label="Sexe" rules={[{ required: true }]}><Select options={[{ value: 'H', label: 'H' }, { value: 'F', label: 'F' }, { value: 'Mixte', label: 'Mixte' }]} /></Form.Item>
          <Form.Item name="capacite" label="Capacité"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="age_min" label="Âge min"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="age_max" label="Âge max"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="semestre_id" label="Semestre" rules={[{ required: true }]}>
            <Select placeholder="Sélectionner un semestre" options={currentSemestre ? [{ value: currentSemestre.id, label: currentSemestre.nom }] : []} disabled={!currentSemestre} />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Drawer>

      {/* Buildings Modal */}
      <Modal title={buildingsResidence ? `Bâtiments — ${buildingsResidence.nom}` : 'Bâtiments'} open={buildingsOpen} onCancel={() => setBuildingsOpen(false)} width={700}
        footer={<div style={{ display: 'flex', gap: 8 }}><button className="btn-outline" onClick={() => setBuildingsOpen(false)}>Fermer</button><button className="btn-primary" onClick={onAddBuilding} disabled={!buildingsResidence}>+ Ajouter un bâtiment</button></div>}>
        {buildingsLoading ? <div style={{ padding: 20, textAlign: 'center' }}>Chargement…</div> : (
          <>
            <div className="table-card"><div className="table-scroll">
              <table>
                <thead><tr><th>Nom</th><th>Capacité (chambres)</th><th>Actions</th></tr></thead>
                <tbody>
                  {pagedBuildingsRows.map((b) => (
                    <tr key={b.id}>
                      <td>{b.nom}</td>
                      <td>{b.capacite_chambres}</td>
                      <td>
                        <div className="actions-cell">
                          <button className="action-btn" title="Modifier" onClick={() => onEditBuilding(b)}><EditIcon /></button>
                          <Popconfirm title="Supprimer ce bâtiment ?" description={b?.nom} okText="Supprimer" cancelText="Annuler" okType="danger" onConfirm={() => onDeleteBuilding(b)}>
                            <button className="action-btn danger" title="Supprimer"><DeleteIcon /></button>
                          </Popconfirm>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div></div>
            <div style={{ marginTop: 8 }}>
              <AntPagination current={buildingsPage} total={buildingsRows.length} pageSize={buildingsPageSize} onChange={setBuildingsPage} />
            </div>
          </>
        )}
      </Modal>

      {/* Building form Modal */}
      <Modal title={buildingEditing ? 'Modifier bâtiment' : 'Nouveau bâtiment'} open={buildingOpen} onCancel={() => setBuildingOpen(false)} onOk={onSubmitBuilding} okText={buildingEditing ? 'Enregistrer' : 'Créer'}>
        <Form form={buildingForm} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true }]}><Input placeholder="A" /></Form.Item>
          <Form.Item name="capacite_chambres" label="Capacité (nombre de chambres)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
